import { useEffect } from "react"
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query"
import { useCounts } from "../components/CountProvider"
import { toast } from "../components/ui/toast"
import {
  api,
  type CreateDashboardDto,
  type UpdateDashboardDto,
  type CreateAccountDto,
  type UpdateAccountDto,
  type CreateProxyDto,
  type UpdateProxyDto,
  type CreateClientDto,
  type UpdateClientDto,
  type CreateUserDto,
  type CreateMailDto,
  type UpdateMailDto,
  type UpdateMailContentDto,
  type CreateLogDto,
  type CreateProfileDto,
  type UpdateProfileDto,
} from "./api"
import type {
  DashboardRecord,
  Account,
  Client,
  Listing,
  Profile,
  Job,
  SiteVisitStat,
} from "../types"
import type {
  AccountOption,
  ProxyOption,
  ClientOption,
  UserRecord,
  MailTemplateRecord,
  LogRecord,
  OperatorRecord,
  WorkerActivityRecord,
  WorkerDailyRecord,
  WorkerUserHistory,
  UserClientsResponse,
  BotSettings,
  UpdateBotSettingsDto,
  BotClientChat,
  UpdateBotClientChatDto,
  ScheduledMessage,
  ScheduleBotMessageDto,
  TestBotMessageDto,
} from "./api"

export const qk = {
  dashboard: ["dashboard"] as const,
  archived: ["archived"] as const,
  accounts: ["accounts"] as const,
  accountOptions: ["accountOptions"] as const,
  proxies: ["proxies"] as const,
  clients: ["clients"] as const,
  operators: ["operators"] as const,
  operatorsFull: ["operatorsFull"] as const,
  users: ["users"] as const,
  userClients: ["userClients"] as const,
  mails: ["mails"] as const,
  logs: ["logs"] as const,
  listings: ["listings"] as const,
  listingStats: ["listingStats"] as const,
  profiles: ["profiles"] as const,
  jobs: ["jobs"] as const,
  jobStats: ["jobStats"] as const,
  siteVisits: ["siteVisits"] as const,
  workerActivity: ["workerActivity"] as const,
  workerHistory: ["workerHistory"] as const,
  workerUserHistory: ["workerUserHistory"] as const,
  botSettings: ["botSettings"] as const,
  botClients: ["botClients"] as const,
  botMessages: ["botMessages"] as const,
}

function useSyncCount(key: keyof ReturnType<typeof useCounts>["counts"], len: number | undefined) {
  const { setCount } = useCounts()
  useEffect(() => {
    if (len !== undefined) setCount(key, len)
  }, [len, key, setCount])
}

export function useDashboardRecords() {
  const query = useQuery<DashboardRecord[], Error>({
    queryKey: qk.dashboard,
    queryFn: api.getDashboard,
  })
  useSyncCount("dashboard", query.data?.length)
  return query
}

export function useArchivedRecords() {
  const query = useQuery<DashboardRecord[], Error>({
    queryKey: qk.archived,
    queryFn: api.getArchived,
  })
  useSyncCount("archive", query.data?.length)
  return query
}

export function useAccounts() {
  const query = useQuery<Account[], Error>({ queryKey: qk.accounts, queryFn: api.getAllAccounts })
  useSyncCount("accounts", query.data?.length)
  return query
}

export function useAccountOptions() {
  return useQuery<AccountOption[], Error>({ queryKey: qk.accountOptions, queryFn: api.getAccounts })
}

export function useProxies() {
  return useQuery<ProxyOption[], Error>({ queryKey: qk.proxies, queryFn: api.getProxies })
}

export function useClients() {
  const query = useQuery<Client[], Error>({ queryKey: qk.clients, queryFn: api.getAllClients })
  useSyncCount("clients", query.data?.length)
  return query
}

export function useOperators() {
  return useQuery<string[], Error>({ queryKey: qk.operators, queryFn: api.getOperators })
}

export function useUsers() {
  const query = useQuery<UserRecord[], Error>({ queryKey: qk.users, queryFn: api.getUsers })
  useSyncCount("users", query.data?.length)
  return query
}

export function useUserClients(userId: number | null, enabled = true) {
  return useQuery<UserClientsResponse, Error>({
    queryKey: [...qk.userClients, userId],
    queryFn: () => api.getUserClients(userId as number),
    enabled: enabled && userId != null,
  })
}

export function useCreateDashboardRecord() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (dto: CreateDashboardDto) => api.createDashboard(dto),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: qk.dashboard })
      toast("Dashboard record created")
    },
    onError: (err) => toast(err.message, "error"),
  })
}

export function useUpdateDashboardRecord() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ id, dto }: { id: number; dto: UpdateDashboardDto }) =>
      api.updateDashboard(id, dto),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: qk.dashboard })
      toast("Dashboard record updated")
    },
    onError: (err) => toast(err.message, "error"),
  })
}

export function useArchiveDashboardRecord() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (id: number) => api.archiveDashboard(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: qk.dashboard })
      qc.invalidateQueries({ queryKey: qk.archived })
      toast("Record archived")
    },
    onError: (err) => toast(err.message, "error"),
  })
}

export function useDeleteDashboardRecord() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (id: number) => api.deleteDashboard(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: qk.archived })
      qc.invalidateQueries({ queryKey: qk.dashboard })
      toast("Record deleted")
    },
    onError: (err) => toast(err.message, "error"),
  })
}

export function useDeleteDashboardRecords() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (ids: number[]) => {
      const results = await Promise.allSettled(ids.map((id) => api.deleteDashboard(id)))
      const deleted = results.filter((r) => r.status === "fulfilled").length
      const failed = results.filter((r): r is PromiseRejectedResult => r.status === "rejected")
      // Nothing deleted at all -> surface the first error to the caller
      if (deleted === 0 && failed.length > 0) throw failed[0].reason
      return { deleted, failed: failed.length }
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: qk.archived })
      qc.invalidateQueries({ queryKey: qk.dashboard })
    },
    onError: (err) => toast(err instanceof Error ? err.message : String(err), "error"),
  })
}

export function useCreateAccount() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (dto: CreateAccountDto) => api.createAccount(dto),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: qk.accounts })
      toast("Account created")
    },
    onError: (err) => toast(err.message, "error"),
  })
}

export function useUpdateAccount() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ id, dto }: { id: number; dto: UpdateAccountDto }) => api.updateAccount(id, dto),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: qk.accounts })
      toast("Account updated")
    },
    onError: (err) => toast(err.message, "error"),
  })
}

export function useRevealAccountPassword() {
  return useMutation({
    mutationFn: ({ id, secretCode }: { id: number; secretCode?: string }) =>
      api.revealAccountPassword(id, secretCode),
    onError: (err) => toast(err.message, "error"),
  })
}

export function useArchiveAccount() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (id: number) => api.archiveAccount(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: qk.accounts })
      toast("Account archived")
    },
    onError: (err) => toast(err.message, "error"),
  })
}

export function useDeleteAccount() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (id: number) => api.deleteAccount(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: qk.accounts })
      toast("Account deleted")
    },
    onError: (err) => toast(err.message, "error"),
  })
}

export function useCreateProxy() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (dto: CreateProxyDto) => api.createProxy(dto),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: qk.proxies })
      qc.invalidateQueries({ queryKey: qk.accounts })
      toast("Proxy created")
    },
    onError: (err) => toast(err.message, "error"),
  })
}

export function useUpdateProxy() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ id, dto }: { id: number; dto: UpdateProxyDto }) => api.updateProxy(id, dto),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: qk.proxies })
      qc.invalidateQueries({ queryKey: qk.accounts })
      toast("Proxy updated")
    },
    onError: (err) => toast(err.message, "error"),
  })
}

export function useDeleteProxy() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (id: number) => api.deleteProxy(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: qk.proxies })
      qc.invalidateQueries({ queryKey: qk.accounts })
      toast("Proxy deleted")
    },
    onError: (err) => toast(err.message, "error"),
  })
}

export function useCreateClient() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (dto: CreateClientDto) => api.createClient(dto),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: qk.clients })
      toast("Client created")
    },
    onError: (err) => toast(err.message, "error"),
  })
}

export function useUpdateClient() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ id, dto }: { id: number; dto: UpdateClientDto }) => api.updateClient(id, dto),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: qk.clients })
      toast("Client updated")
    },
    onError: (err) => toast(err.message, "error"),
  })
}

export function useCreateUser() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (dto: CreateUserDto) => api.createUser(dto),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: qk.users })
      toast("User created")
    },
    onError: (err) => toast(err.message, "error"),
  })
}

export function useDeleteUser() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (id: number) => api.deleteUser(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: qk.users })
      toast("User deleted")
    },
    onError: (err) => toast(err.message, "error"),
  })
}

export function useSetUserEnabled() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ id, enabled }: { id: number; enabled: boolean }) =>
      api.setUserEnabled(id, enabled),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: qk.users })
      qc.invalidateQueries({ queryKey: qk.operatorsFull })
      toast("User status updated")
    },
    onError: (err) => toast(err.message, "error"),
  })
}

export function useGenerateUserSecretCode() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (id: number) => api.generateUserSecretCode(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: qk.users })
      toast("Operator secret code generated")
    },
    onError: (err) => toast(err.message, "error"),
  })
}

export function useOperatorsFull() {
  return useQuery<OperatorRecord[], Error>({
    queryKey: qk.operatorsFull,
    queryFn: api.getOperatorsFull,
  })
}

export function useMails() {
  const query = useQuery<MailTemplateRecord[], Error>({ queryKey: qk.mails, queryFn: api.getMails })
  useSyncCount("mails", query.data?.length)
  return query
}

export function useCreateMail() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (dto: CreateMailDto) => api.createMail(dto),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: qk.mails })
      toast("Mail template created")
    },
    onError: (err) => toast(err.message, "error"),
  })
}

export function useUpdateMail() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ id, dto }: { id: number; dto: UpdateMailDto }) => api.updateMail(id, dto),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: qk.mails })
      toast("Mail template updated")
    },
    onError: (err) => toast(err.message, "error"),
  })
}

export function useUpdateMailContent() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({
      templateId,
      mailId,
      dto,
    }: {
      templateId: number
      mailId: number
      dto: UpdateMailContentDto
    }) => api.updateMailContent(templateId, mailId, dto),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: qk.mails })
      toast("Mail content updated")
    },
    onError: (err) => toast(err.message, "error"),
  })
}

export function useCreateLog() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (dto: CreateLogDto) => api.createLog(dto),
    onSuccess: () => qc.invalidateQueries({ queryKey: qk.logs }),
    onError: (err) => toast(err.message, "error"),
  })
}

export function useLogs() {
  const query = useQuery<LogRecord[], Error>({ queryKey: qk.logs, queryFn: api.getLogs })
  useSyncCount("logs", query.data?.length)
  return query
}

export function useListings(params?: { status?: string; sourceName?: string }) {
  return useQuery<{ items: Listing[]; total: number }, Error>({
    queryKey: [...qk.listings, params],
    queryFn: () => api.getListings(params),
  })
}

export function useListingStats() {
  return useQuery<
    { total: number; new: number; matched: number; processed: number; skipped: number },
    Error
  >({
    queryKey: qk.listingStats,
    queryFn: api.getListingStats,
  })
}

export function useProfiles() {
  return useQuery<Profile[], Error>({ queryKey: qk.profiles, queryFn: api.getProfiles })
}

export function useCreateProfile() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (dto: CreateProfileDto) => api.createProfile(dto),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: qk.profiles })
      toast("Profile created")
    },
    onError: (err) => toast(err.message, "error"),
  })
}

export function useUpdateProfile() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ id, dto }: { id: number; dto: UpdateProfileDto }) => api.updateProfile(id, dto),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: qk.profiles })
      toast("Profile updated")
    },
    onError: (err) => toast(err.message, "error"),
  })
}

export function useToggleProfile() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (id: number) => api.toggleProfile(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: qk.profiles })
      toast("Profile toggled")
    },
    onError: (err) => toast(err.message, "error"),
  })
}

export function useDeleteProfile() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (id: number) => api.deleteProfile(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: qk.profiles })
      toast("Profile deleted")
    },
    onError: (err) => toast(err.message, "error"),
  })
}

export function useDeleteAllProfiles() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: () => api.deleteAllProfiles(),
    onSuccess: (data) => {
      qc.invalidateQueries({ queryKey: qk.profiles })
      toast(`Deleted ${data.deleted} profiles`)
    },
    onError: (err) => toast(err.message, "error"),
  })
}

export function useSyncProfilesFromClients() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: () => api.syncProfilesFromClients(),
    onSuccess: (data) => {
      qc.invalidateQueries({ queryKey: qk.profiles })
      toast(`Synced: ${data.created} created, ${data.updated} updated`)
    },
    onError: (err) => toast(err.message, "error"),
  })
}

export function useJobs(params?: { status?: string }) {
  return useQuery<{ items: Job[]; total: number }, Error>({
    queryKey: [...qk.jobs, params],
    queryFn: () => api.getJobs(params),
  })
}

export function useJobStats() {
  return useQuery<
    { total: number; pending: number; claimed: number; completed: number; failed: number },
    Error
  >({
    queryKey: qk.jobStats,
    queryFn: api.getJobStats,
  })
}

export function useClearPendingJobs() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: () => api.clearPendingJobs(),
    onSuccess: (data) => {
      qc.invalidateQueries({ queryKey: qk.jobs })
      qc.invalidateQueries({ queryKey: qk.jobStats })
      toast(`${data.deleted} pending jobs cleared`)
    },
    onError: (err) => toast(err.message, "error"),
  })
}

export function useSiteVisits(days = 30) {
  return useQuery<SiteVisitStat[], Error>({
    queryKey: [...qk.siteVisits, days],
    queryFn: () => api.getSiteVisits(days),
  })
}

export function useWorkerActivity(refetchInterval = 10000) {
  return useQuery<WorkerActivityRecord[], Error>({
    queryKey: qk.workerActivity,
    queryFn: api.getWorkerActivity,
    refetchInterval,
  })
}

export function useWorkerActivityHistory(days = 30, enabled = true) {
  return useQuery<WorkerDailyRecord[], Error>({
    queryKey: [...qk.workerHistory, days],
    queryFn: () => api.getWorkerActivityHistory(days),
    enabled,
  })
}

export function useWorkerActivityHistoryForUser(userId: number | null, days = 30, enabled = true) {
  return useQuery<WorkerUserHistory, Error>({
    queryKey: [...qk.workerUserHistory, userId, days],
    queryFn: () => api.getWorkerActivityHistoryForUser(userId as number, days),
    enabled: enabled && userId != null,
  })
}

export function useBotSettings() {
  return useQuery<BotSettings, Error>({
    queryKey: qk.botSettings,
    queryFn: () => api.getBotSettings(),
  })
}

export function useUpdateBotSettings() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (dto: UpdateBotSettingsDto) => api.updateBotSettings(dto),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: qk.botSettings })
      qc.invalidateQueries({ queryKey: qk.botClients })
      toast("Bot settings saved")
    },
    onError: (err) => toast(err.message, "error"),
  })
}

export function useBotClients() {
  return useQuery<BotClientChat[], Error>({
    queryKey: qk.botClients,
    queryFn: () => api.getBotClients(),
  })
}

export function useUpdateBotClientChat() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ clientId, dto }: { clientId: number; dto: UpdateBotClientChatDto }) =>
      api.updateBotClientChat(clientId, dto),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: qk.botClients })
      toast("Client chat saved")
    },
    onError: (err) => toast(err.message, "error"),
  })
}

export function useBotMessages() {
  return useQuery<ScheduledMessage[], Error>({
    queryKey: qk.botMessages,
    queryFn: () => api.getBotMessages(),
    refetchInterval: 30000,
  })
}

export function useScheduleBotMessage() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (dto: ScheduleBotMessageDto) => api.scheduleBotMessage(dto),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: qk.botMessages })
      toast("Message scheduled")
    },
    onError: (err) => toast(err.message, "error"),
  })
}

export function useCancelBotMessage() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (id: number) => api.cancelBotMessage(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: qk.botMessages })
      toast("Scheduled message cancelled")
    },
    onError: (err) => toast(err.message, "error"),
  })
}

export function useTestBotMessage() {
  return useMutation({
    mutationFn: (dto: TestBotMessageDto) => api.testBotMessage(dto),
    onSuccess: () => toast("Test message sent"),
    onError: (err) => toast(err.message, "error"),
  })
}
